import Button from './Button';

// Janela de páginas: primeira e última sempre visíveis, vizinhas da página
// atual no meio e reticências nos saltos (máx. 7 itens).
function buildPaginationItems(currentPage, totalPages) {
    if (totalPages <= 7) {
        return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    const items = [1];
    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);

    if (start > 2) {
        items.push('ellipsis-start');
    }

    for (let page = start; page <= end; page++) {
        items.push(page);
    }

    if (end < totalPages - 1) {
        items.push('ellipsis-end');
    }

    items.push(totalPages);
    return items;
}

/**
 * Implementação ÚNICA de paginação do design system — a StandardTable renderiza
 * este mesmo componente no footer dela; não duplicar a lógica em outro lugar.
 *
 * Uso standalone (default) mantém o container com borda superior + resumo
 * "Mostrando X a Y de Z". Props opcionais pra embutir em outro layout:
 *   showSummary  false esconde o resumo (ex.: quando o container pai já mostra o range).
 *   className    substitui as classes do container padrão.
 */
const Pagination = ({
    currentPage,
    totalPages,
    onPageChange,
    itemsPerPage = 50,
    totalItems,
    showSummary = true,
    className = 'flex items-center justify-between px-6 py-4 border-t border-mono-200 dark:border-mono-800',
}) => {
    const safeTotalPages = Math.max(1, Number(totalPages) || 1);
    const safeCurrentPage = Math.min(Math.max(1, Number(currentPage) || 1), safeTotalPages);
    const paginationItems = buildPaginationItems(safeCurrentPage, safeTotalPages);

    const totalCount = Math.max(0, Number(totalItems) || 0);
    const startItem = totalCount === 0 ? 0 : (safeCurrentPage - 1) * itemsPerPage + 1;
    const endItem = Math.min(safeCurrentPage * itemsPerPage, totalCount);

    return (
        <div className={className}>
            {showSummary ? (
                <div className="text-sm text-mono-600 dark:text-mono-400">
                    Mostrando <span className="font-medium text-black dark:text-white">{startItem}</span> a{' '}
                    <span className="font-medium text-black dark:text-white">{endItem}</span> de{' '}
                    <span className="font-medium text-black dark:text-white">{totalCount}</span> resultados
                </div>
            ) : null}

            <nav aria-label="Paginação" className="flex flex-wrap items-center gap-2">
                <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => onPageChange(safeCurrentPage - 1)}
                    disabled={safeCurrentPage <= 1}
                >
                    Anterior
                </Button>

                {paginationItems.map((item) => {
                    // Reticências vêm como 'ellipsis-start'/'ellipsis-end' (únicas na lista).
                    if (typeof item !== 'number') {
                        return (
                            <span key={item} className="px-1 text-sm text-mono-400 dark:text-mono-500">
                                ...
                            </span>
                        );
                    }

                    return (
                        <Button
                            key={item}
                            variant={item === safeCurrentPage ? 'primary' : 'ghost'}
                            size="sm"
                            className="min-w-[2.25rem]"
                            aria-current={item === safeCurrentPage ? 'page' : undefined}
                            onClick={() => onPageChange(item)}
                        >
                            {item}
                        </Button>
                    );
                })}

                <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => onPageChange(safeCurrentPage + 1)}
                    disabled={safeCurrentPage >= safeTotalPages}
                >
                    Proxima
                </Button>
            </nav>
        </div>
    );
};

export default Pagination;
